<template>
  <b-form @submit.prevent="saveSkill" class="skill-form">
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="mb-1 font-weight-bold" v-if="title">{{ title }}</h2>
        <p class="text-muted mb-0" v-if="description">
          {{ description }}
        </p>
      </div>

      <b-button
        variant="primary"
        @click="addSkill()"
        class="rounded px-1 d-flex align-items-center"
      >
        <feather-icon icon="PlusIcon" size="18" class="mr-50" />
        Add Skill
      </b-button>
    </div>

    <h4 class="mb-3 font-weight-bold" v-if="profile">Manage Skills</h4>

    <!-- Skill Card -->
    <div
      v-for="(skill, index) in form.skills"
      :key="index"
      class="skill-card mb-3"
      ref="skillCards"
    >
      <!-- Card Header -->
      <div
        class="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2"
      >
        <div class="d-flex align-items-center">
          <div class="skill-number mr-1">{{ index + 1 }}</div>
          <div>
            <h5 class="mb-0 font-weight-semibold">
              <div class="form-group">
                <div class="skill-type-badge">
                  {{ getSkillType(getSelectedSkillType(skill.skill_id)) }}
                  Information
                </div>
              </div>
            </h5>
          </div>
        </div>

        <b-button
          variant="danger"
          size="sm"
          class="rounded"
          @click="removeSkill(index)"
          :disabled="form.skills.length == 1"
        >
          <feather-icon icon="Trash2Icon" size="16" />
        </b-button>
      </div>

      <!-- Form Row -->
      <b-row>
        <!-- Skill -->
        <b-col cols="12" lg="4">
          <div class="form-group">
            <label class="font-weight-semibold"> Skill </label>

            <Select
              v-model="skill.skill_id"
              filterable
              placeholder="Select Skill"
            >
              <Option
                v-if="filteredSkillOptions(index).length"
                v-for="item in filteredSkillOptions(index)"
                :value="item.id"
                :key="item.id"
              >
                {{ getSkillType(item.type) }} - {{ item.name }}
              </Option>
            </Select>

            <small
              v-if="form.errors.has('skills.' + index + '.skill_id')"
              class="text-danger"
            >
              This field is required
            </small>
          </div>
        </b-col>

        <!-- Proficiency -->
        <b-col cols="12" lg="3">
          <div class="form-group">
            <label class="font-weight-semibold"> Proficiency </label>

            <Select
              v-model="skill.proficiency"
              filterable
              placeholder="Select Proficiency"
            >
              <Option
                v-for="item in proficiency"
                :value="item.name"
                :key="item.name"
              >
                {{ item.name }}
              </Option>
            </Select>

            <small
              v-if="form.errors.has('skills.' + index + '.proficiency')"
              class="text-danger"
            >
              This field is required
            </small>
          </div>
        </b-col>

        <!-- Experience -->
        <b-col cols="12" lg="3">
          <div class="form-group">
            <label class="font-weight-semibold"> Experience (Years) </label>

            <InputNumber
              v-model="skill.years_of_experience"
              placeholder="0"
              style="width: 100%"
              :min="0"
            />

            <small
              v-if="form.errors.has('skills.' + index + '.years_of_experience')"
              class="text-danger"
            >
              This field is required
            </small>
          </div>
        </b-col>

        <!-- Details -->
        <b-col cols="12" v-if="getSelectedSkillType(skill.skill_id) === 2">
          <div class="form-group mt-1">
            <label class="font-weight-semibold"> Skill Details </label>

            <div class="editor-wrapper">
              <quill-editor
                v-model="skill.skill_details"
                :options="editorOptions"
              ></quill-editor>
            </div>

            <has-error :form="form" field="skill_details"></has-error>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Footer Buttons -->
    <div class="d-flex justify-content-between mt-4">
      <div>
        <router-link to="/profile" v-if="profile">
          <b-button variant="outline-secondary" class="rounded px-1">
            Back to Profile
          </b-button>
        </router-link>
      </div>

      <div class="d-flex">
        <b-button
          variant="outline-primary"
          @click="addSkill()"
          class="rounded px-1 d-flex align-items-center mr-2"
        >
          <feather-icon icon="PlusIcon" size="18" class="mr-50" />
          Add More Skill
        </b-button>
        <b-button
          variant="primary"
          type="submit"
          :disabled="form.busy"
          class="rounded px-1"
        >
          Save Skills
        </b-button>

        <b-button
          variant="primary"
          class="rounded px-4 ml-2"
          v-if="showSkillLength > 0 && nextName && !profile"
          @click="nextOnboard(nextName, nextTitle)"
        >
          Next
        </b-button>
      </div>
    </div>
  </b-form>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BForm,
  BButton,
} from "bootstrap-vue";
import { HasError, AlertError } from "vform/src/components/bootstrap5";

export default {
  props: [
    "title",
    "nextOnboard",
    "nextTitle",
    "nextName",
    "description",
    "profile",
  ],
  data() {
    return {
      form: new Form({
        skills: [
          {
            skill_id: null,
            proficiency: null,
            years_of_experience: null,
            skill_details: null,
          },
        ],
      }),
      showSkillLength: null,
      skillsData: [],
      proficiency: [
        { name: "EXPERT" },
        { name: "ADVANCED" },
        { name: "EXPERIENCED" },
        { name: "BEGINNER" },
      ],
      editorOptions: {
        theme: "snow",
        placeholder: "Enter Details...",
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["clean"],
          ],
        },
      },
    };
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    BButton,
    HasError,
    AlertError,
  },
  computed: {
    filteredSkillOptions() {
      return (index) => {
        const selectedSkillIds = this.form.skills
          .slice(0, index)
          .map((skill) => skill.skill_id);
        return this.skillsData.filter(
          (skill) => !selectedSkillIds.includes(skill.id),
        );
      };
    },
  },
  mounted() {
    this.getSkill();
    this.showSkill();
  },
  methods: {
    getSkill() {
      axios
        .get("/candidate/skills")
        .then((res) => {
          this.skillsData = res.data.skills;
        })
        .catch((e) => {
          this.e(e.response.data.error);
        });
    },
    showSkill() {
      axios
        .get("/candidate/skills/show")
        .then((res) => {
          let skills = res.data.skills;
          let length = skills.length;
          if (length > 0) {
            this.$store.dispatch("onboardData");
            this.showSkillLength = length;
            this.form.skills = [];
            skills.forEach((data) => {
              this.form.skills.push({
                skill_id: data.skill_id,
                proficiency: data.proficiency,
                years_of_experience: data.years_of_experience,
                skill_details: data.skill_details,
              });
            });
          }
        })
        .catch((e) => {
          this.e(e.response.data.error);
        });
    },
    getSkillType(type) {
      switch (type) {
        case 1:
          return "Soft Skill";
        case 2:
          return "Hard Skill";
        case 3:
          return "Software Knowledge";
        default:
          return "";
      }
    },
    saveSkill() {
      this.form
        .post("/candidate/skills/store")
        .then((res) => {
          this.showSkill();
          this.s(res.data.message);
        })
        .catch((e) => {
          this.e(e.response.data.message);
        });
    },
    addSkill() {
      this.form.skills.push({
        skill_id: null,
        proficiency: null,
        years_of_experience: null,
        skill_details: null,
      });
      this.$nextTick(() => {
        const cards = this.$refs.skillCards;

        if (cards && cards.length) {
          const lastCard = cards[cards.length - 1];

          lastCard.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
    removeSkill(index) {
      this.form.skills.splice(index, 1);
    },

    getSelectedSkillType(skillId) {
      const skill = this.skillsData.find((item) => item.id === skillId);
      return skill ? skill.type : null;
    },
  },
};
</script>
<style scoped>
.skill-form {
  width: 100%;
}

.skill-card {
  background: #fff;
  border-radius: 18px;
  padding: 24px;
  border: 1px solid #ebeef5;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
  transition: 0.3s ease;
}

.skill-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);
}

.skill-number {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.skill-type-badge {
  background: #f4f5f7;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #5e5873;
  min-height: 44px;
  display: flex;
  align-items: center;
}

.editor-wrapper {
  border: 1px solid #d8d6de;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.ql-toolbar.ql-snow {
  border-top: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
}

.ql-container.ql-snow {
  border: 0 !important;
}
</style>
